// src/App.js
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';

// Direkt geladene Komponenten (immer benötigt)
import Auth from './components/Auth';
import AuthGuard from './components/AuthGuard';
import OwnerGuard from './components/OwnerGuard';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';

// Globale Context-Provider
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';

// Globales Basis-CSS
import './styles/base.css';
// Die Navigation (Navbar, DMNavbar, Seitenleiste) steht auf jeder Seite, ihr
// CSS hing aber an den einzelnen Komponenten. Auf /schmiede kam es deshalb
// nie an: Die Seite importiert nur schmiede.css, rendert aber <Navbar />.
// Geprueft mit der Routenpruefung in docs/AUDIT.md, Abschnitt 26.
import './styles/layout.css';

// ============================================
// Lazy-loaded Komponenten (on-demand)
// ============================================

// Spieler-Komponenten
const CharacterSelection = lazy(() => import('./components/CharacterSelection'));
const CharacterCreate = lazy(() => import('./components/CharacterCreate'));
const HeldenImport = lazy(() => import('./components/HeldenImport'));
const CharacterDetail = lazy(() => import('./components/CharacterDetail'));
const Battle = lazy(() => import('./components/battle'));
const Inventory = lazy(() => import('./components/Inventory.jsx'));
const Schmiede = lazy(() => import('./components/Schmiede'));
const CharacterNotebook = lazy(() => import('./components/CharacterNotebook.jsx'));
const Questbook = lazy(() => import('./components/Questbook.jsx'));
const Spellbook = lazy(() => import('./components/Zauber'));
const Liturgien = lazy(() => import('./components/Liturgien'));
const Companions = lazy(() => import('./components/Companions'));

// DM-Komponenten (nur wenn DM-Bereich betreten wird)
const DMLayout = lazy(() => import('./components/MasterArea/DMLayout.jsx'));
const DMPage = lazy(() => import('./components/MasterArea/index.jsx'));
const DMShop = lazy(() => import('./components/MasterArea/shop/ShopSeite'));
const DMLootManager = lazy(() => import('./components/MasterArea/LootManager.jsx'));
const DMLogs = lazy(() => import('./components/MasterArea/logs.jsx'));
const DMGroupManager = lazy(() => import('./components/MasterArea/GroupManager.jsx'));
const UserRoleManager = lazy(() => import('./components/MasterArea/UserRoleManager.jsx'));
const APManager = lazy(() => import('./components/MasterArea/APManager.jsx'));
const GroupLeveling = lazy(() => import('./components/MasterArea/GroupLeveling.jsx'));
const HexMapManager = lazy(() => import('./components/MasterArea/HexMapManager.jsx'));
const HexMapViewer = lazy(() => import('./components/MasterArea/HexMapViewer.jsx'));
const DMQuestbook = lazy(() => import('./components/MasterArea/DMQuestbook.jsx'));
const InitiativeTracker = lazy(() => import('./components/MasterArea/InitiativeTracker'));
const DMNpcNotes = lazy(() => import('./components/MasterArea/DMNpcNotes.jsx'));
const NscBoegen = lazy(() => import('./components/MasterArea/NscBoegen'));
const Handouts = lazy(() => import('./components/MasterArea/Handouts'));
const Kalender = lazy(() => import('./components/MasterArea/Kalender'));
const SpielTafel = lazy(() => import('./components/SpielTafel'));

const App = () => {
  useEffect(() => {
    function setFullHeight() {
      document.body.style.height = window.innerHeight + 'px';
    }
    setFullHeight();
    window.addEventListener('resize', setFullHeight);
    window.addEventListener('orientationchange', setFullHeight);
    return () => {
      window.removeEventListener('resize', setFullHeight);
      window.removeEventListener('orientationchange', setFullHeight);
    };
  }, []);

  return (
    <AuthProvider>
      <ToastProvider>
        <div>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/" element={<Auth />} />
                  {/* Spieler-Routen (geschützt) */}
                  <Route
                    path="/character"
                    element={
                      <AuthGuard>
                        <CharacterSelection />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/new"
                    element={
                      <AuthGuard>
                        <CharacterCreate />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/import"
                    element={
                      <AuthGuard>
                        <HeldenImport />
                      </AuthGuard>
                    }
                  />
                  {/* Datum und freigegebene Handouts – für alle am Tisch. */}
                  <Route
                    path="/tisch"
                    element={
                      <AuthGuard>
                        <SpielTafel />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <CharacterDetail />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Kampf"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <Battle />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Schmiede"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <Schmiede />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Zauber"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <Spellbook />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Liturgien"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <Liturgien />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Questbuch"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <Questbook />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Notizbuch"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <CharacterNotebook />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Inventar"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <Inventory />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/character/:id/Begleiter"
                    element={
                      <AuthGuard>
                        <OwnerGuard>
                          <Companions />
                        </OwnerGuard>
                      </AuthGuard>
                    }
                  />
                  {/* DM-Routen (geschützt, erfordert GM-Rolle) */}
                  <Route
                    path="/dm/hexmap/viewer"
                    element={
                      <AuthGuard requiredRole="gm">
                        <HexMapViewer />
                      </AuthGuard>
                    }
                  />
                  <Route
                    path="/dm"
                    element={
                      <AuthGuard requiredRole="gm">
                        <DMLayout />
                      </AuthGuard>
                    }
                  >
                    <Route index element={<DMPage />} />
                    <Route path="shop" element={<DMShop />} />
                    <Route path="groups" element={<DMGroupManager />} />
                    <Route path="inventory" element={<DMLootManager />} />
                    <Route path="ap" element={<APManager />} />
                    <Route path="leveling" element={<GroupLeveling />} />
                    <Route path="hexmap" element={<HexMapManager />} />
                    <Route path="logs" element={<DMLogs />} />
                    <Route path="rollen" element={<UserRoleManager />} />
                    <Route path="quests" element={<DMQuestbook />} />
                    <Route path="npcs" element={<DMNpcNotes />} />
                    <Route path="nsc" element={<NscBoegen />} />
                    <Route path="handouts" element={<Handouts />} />
                    <Route path="kalender" element={<Kalender />} />
                    <Route path="initiative" element={<InitiativeTracker />} />
                  </Route>
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Router>
        </div>
      </ToastProvider>
    </AuthProvider>
  );
};

export default App;
